import { Pipe, PipeTransform } from '@angular/core';

/**
 * Formatea una duración en segundos como texto, p. ej. `5400 | timeFormat` → "1 hr 30 m".
 *
 * @param showMinutes Incluir minutos (default: true)
 * @param showSeconds Incluir segundos (default: false)
 * @param addSpace Separar número y unidad: "1 hr" vs "1hr" (default: false)
 * @param hourFormat Etiqueta de horas: `h`, `hr` o `hrs` (`hrs` usa "hr" en singular)
 */
@Pipe({
  name: 'timeFormat',
  standalone: true,
})
export class TimeFormatPipe implements PipeTransform {
  transform(
    seconds: number | null | undefined,
    showMinutes: boolean = true,
    showSeconds: boolean = false,
    addSpace: boolean = false,
    hourFormat: 'h' | 'hr' | 'hrs' = 'hrs',
  ): string {
    if (seconds == null || seconds === 0) {
      const space = addSpace ? ' ' : '';
      const hourLabel = this.getHourLabel(0, hourFormat);
      return showMinutes
        ? `0${space}${hourLabel}${space}0${space}m`
        : `0${space}${hourLabel}`;
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    const parts = [];
    const space = addSpace ? ' ' : '';

    if (hours > 0) {
      const hourLabel = this.getHourLabel(hours, hourFormat);
      parts.push(`${this.formatNumber(hours)}${space}${hourLabel}`);
    }

    if (showMinutes && minutes > 0) {
      parts.push(`${this.formatNumber(minutes)}${space}m`);
    }

    if (showSeconds && secs > 0) {
      parts.push(`${this.formatNumber(secs)}${space}s`);
    }

    if (parts.length === 0) {
      const hourLabel = this.getHourLabel(0, hourFormat);
      return showMinutes
        ? `0${space}${hourLabel}${space}0${space}m`
        : `0${space}${hourLabel}`;
    }

    return parts.join(' ');
  }

  private getHourLabel(hours: number, format: 'h' | 'hr' | 'hrs'): string {
    switch (format) {
      case 'h':
        return 'h';
      case 'hr':
        return 'hr';
      case 'hrs':
      default:
        return hours === 1 ? 'hr' : 'hrs';
    }
  }

  private formatNumber(value: number): string {
    return value.toLocaleString('en-US');
  }
}
